// "Mein Kalender einbinden" panel — used in builder + voter.
//
// Privacy is the headline feature: nothing leaves the browser, nothing is
// stored. The user pastes (or drag-drops) ICS content; we parse it locally
// and emit busy-event ranges. When the user closes the tab, everything goes.

import JSZip from 'jszip';
import { parseICS, type BusyEvent } from '../ics-parser';
import { el, clear } from './dom';

export type CalendarPanelOptions = {
  /** Called whenever the parsed busy-event set changes. */
  onUpdate: (events: BusyEvent[]) => void;
};

export function renderCalendarPanel(opts: CalendarPanelOptions): HTMLElement {
  // Default: kompakter Einzeiler mit Drop-Target. Aufklappen per Klick.
  // Sobald ein Kalender importiert ist, wechselt die Ansicht auf den
  // bestehenden „X Termine geladen"-Compact-Bar (siehe weiter unten).
  const root = el('section', { class: 'calendar-panel is-collapsed-empty', 'aria-label': 'Eigener Kalender' });

  // Einzeiler — sichtbar, solange noch nichts importiert UND Panel zu ist.
  const collapsedBar = el('div', { class: 'calendar-collapsed-bar', tabindex: '0', role: 'button' });
  collapsedBar.append(
    el('span', { class: 'calendar-collapsed-icon' }, '📅'),
    el('span', { class: 'calendar-collapsed-text' },
      el('strong', {}, 'Mein Kalender'),
      ' — ',
      el('span', { class: 'calendar-collapsed-hint' }, '.ics oder Google-ZIP hier reinziehen oder klicken zum Aufklappen'),
    ),
    el('span', { class: 'calendar-collapsed-privacy' }, '🔒 nur in deinem Browser'),
    el('span', { class: 'calendar-collapsed-chevron' }, '▾'),
  );
  root.append(collapsedBar);
  const expand = () => root.classList.remove('is-collapsed-empty');
  collapsedBar.addEventListener('click', expand);
  collapsedBar.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); expand(); }
  });

  const header = el('header', { class: 'calendar-panel-header' });
  const titleRow = el('div', { class: 'calendar-panel-title-row' });
  // Kleines Icon-Link „nur für Profis" — klappt eine versteckte Textarea
  // mit Paste-Feld und Knopf auf. Default-User soll es nicht sehen.
  const advancedToggle = el('button',
    { type: 'button', class: 'calendar-advanced-toggle', title: 'Alternativ: ICS-Quelltext direkt einfügen' },
    '{…}',
  ) as HTMLButtonElement;
  // Collapse-Knopf direkt am Titel — User kann das Panel jederzeit zumachen.
  const collapseBtn = el('button',
    { type: 'button', class: 'calendar-collapse-btn', title: 'Einklappen', 'aria-label': 'Einklappen' },
    '▴',
  ) as HTMLButtonElement;
  collapseBtn.addEventListener('click', () => root.classList.add('is-collapsed-empty'));

  titleRow.append(
    el('span', { class: 'calendar-panel-icon' }, '🔒'),
    el('h2', { class: 'calendar-panel-title' }, 'Mein Kalender (optional)'),
    el('span', { class: 'calendar-privacy-badge' }, 'Privacy-First'),
    advancedToggle,
    collapseBtn,
  );
  header.append(titleRow);
  header.append(
    el('p', { class: 'calendar-panel-privacy' },
      'Dein Kalender bleibt ',
      el('strong', {}, 'ausschließlich in deinem Browser'),
      '. Wir senden ihn nicht an unseren Server, speichern nichts und sehen nichts davon. ',
      'Tab schließen = vergessen.',
    ),
  );
  root.append(header);

  const body = el('div', { class: 'calendar-panel-body' });
  root.append(body);

  // Status / loaded summary
  const status = el('p', { class: 'calendar-status' });
  status.style.display = 'none';

  // Drop / paste area + hidden file input for click-to-pick
  const fileInput = el('input', {
    type: 'file',
    class: 'calendar-file-input',
    accept: '.ics,.zip,text/calendar,application/zip',
    multiple: 'multiple',
  }) as HTMLInputElement;
  fileInput.style.display = 'none';

  const dropArea = el('div', { class: 'calendar-drop-area', tabindex: '0', role: 'button' });
  dropArea.append(
    el('p', { class: 'calendar-drop-hint' },
      el('strong', {}, '.ics- oder .zip-Datei'),
      ' hierher ziehen ',
      el('span', { class: 'calendar-drop-or' }, 'oder klicken zum Auswählen'),
    ),
    el('p', { class: 'calendar-drop-multi' },
      'Google-Export-ZIP geht direkt — Slot entpackt im Browser. Mehrere Dateien gleichzeitig möglich.',
    ),
  );
  dropArea.addEventListener('click', () => fileInput.click());
  dropArea.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  // Paste-Feld + Anwenden-Knopf liegen in einem versteckten Profi-Block,
  // der nur über den {…}-Toggle in der Überschrift aufgeht. Standard-User
  // sieht ihn nie — kein UI-Lärm für eine extrem seltene Eingabeart.
  const textarea = el('textarea', {
    class: 'calendar-paste',
    placeholder: 'ICS-Quelltext hier einfügen (BEGIN:VCALENDAR …)',
    rows: 3,
    spellcheck: 'false',
  }) as HTMLTextAreaElement;

  const applyBtn = el('button',
    { type: 'button', class: 'btn-primary calendar-apply' },
    'Eingefügten Text einlesen',
  ) as HTMLButtonElement;

  const advancedBlock = el('div', { class: 'calendar-advanced-block' },
    el('p', { class: 'calendar-advanced-hint' },
      'Nur für Profis — wenn du nur den ICS-Quelltext zur Hand hast (z.B. aus einer URL-Antwort).',
    ),
    textarea,
    applyBtn,
  );
  advancedBlock.style.display = 'none';
  advancedToggle.addEventListener('click', () => {
    const open = advancedBlock.style.display === 'none';
    advancedBlock.style.display = open ? '' : 'none';
    advancedToggle.classList.toggle('is-open', open);
    if (open) textarea.focus();
  });

  const clearBtn = el('button',
    { type: 'button', class: 'btn-secondary calendar-clear' },
    'Kalender vergessen',
  ) as HTMLButtonElement;
  clearBtn.style.display = 'none';

  const actionsRow = el('div', { class: 'calendar-actions' }, clearBtn);

  body.append(status, dropArea, fileInput, advancedBlock, actionsRow);

  // Help / direct links section
  body.append(renderHelp());

  // ---- State and behaviour ----

  // We accumulate events from multiple files/pastes into one set, indexed by
  // a per-source label. Re-importing the same source replaces only its
  // contribution. "Vergessen" clears everything.
  const sources: Map<string, BusyEvent[]> = new Map();

  // Compact bar shown after a successful import. Replaces the full body so
  // the calendar panel doesn't dominate the layout once it's done its job.
  const compactBar = el('div', { class: 'calendar-compact' });
  compactBar.style.display = 'none';
  root.append(compactBar);

  // Toggle: show event titles INSIDE the busy-overlays in the matrix.
  // We flip a body-level class so all matrix views (builder, voter) react
  // through CSS, without the calendar-panel having to know about them.
  let titlesVisible = false;
  const TITLES_CLASS = 'show-bg-titles';

  const renderCompact = (totalEvents: number) => {
    clear(compactBar);
    const totalSources = sources.size;
    const sourceLabel = totalSources === 1 ? '1 Datei' : `${totalSources} Dateien`;
    compactBar.append(
      el('span', { class: 'calendar-compact-icon' }, '📅'),
      el('span', { class: 'calendar-compact-text' },
        el('strong', {}, `${totalEvents} Termin${totalEvents === 1 ? '' : 'e'}`),
        ` aus ${sourceLabel} geladen — als belegt in der Matrix sichtbar. `,
        el('span', { class: 'calendar-compact-privacy' }, '🔒 nur in deinem Browser.'),
      ),
    );
    const showBtn = el('button',
      { type: 'button', class: 'btn-secondary calendar-compact-show' },
      titlesVisible ? '× Titel ausblenden' : '👁 Titel in den Blöcken zeigen',
    );
    showBtn.addEventListener('click', () => {
      titlesVisible = !titlesVisible;
      document.body.classList.toggle(TITLES_CLASS, titlesVisible);
      renderCompact(totalEvents);
    });
    const editBtn = el('button',
      { type: 'button', class: 'btn-secondary calendar-compact-edit' },
      '✎ Ändern',
    );
    editBtn.addEventListener('click', () => {
      root.classList.remove('is-collapsed');
    });
    const clearCompactBtn = el('button',
      { type: 'button', class: 'btn-secondary calendar-compact-clear' },
      '✕ Vergessen',
    );
    clearCompactBtn.addEventListener('click', () => {
      sources.clear();
      titlesVisible = false;
      document.body.classList.remove(TITLES_CLASS);
      flush({ reset: true });
    });
    compactBar.append(showBtn, editBtn, clearCompactBtn);
  };

  // Wurde schon mal versucht zu importieren? Dann nicht in den schlanken
  // Einzeiler-Default zurückfallen — der versteckt nämlich die Status-/Fehler-
  // Meldung im body, und der User denkt, der Klick „hatte keine Funktion".
  let hasAttemptedImport = false;

  const flush = (opts2?: { reset?: boolean }) => {
    const all: BusyEvent[] = [];
    for (const list of sources.values()) all.push(...list);
    opts.onUpdate(all);
    if (opts2?.reset) hasAttemptedImport = false;
    if (all.length === 0) {
      clearBtn.style.display = 'none';
      compactBar.style.display = 'none';
      root.classList.remove('is-collapsed');
      if (hasAttemptedImport) {
        // Nach gescheitertem/leerem Import expanded bleiben, damit der
        // Status-Hinweis (Fehler oder „0 Termine") sichtbar ist.
        root.classList.remove('is-collapsed-empty');
      } else {
        status.style.display = 'none';
        // Initial-Zustand oder nach „Vergessen": schlanker Einzeiler.
        root.classList.add('is-collapsed-empty');
      }
    } else {
      const totalSources = sources.size;
      const sourceLabel = totalSources === 1 ? '1 Quelle' : `${totalSources} Quellen`;
      showSuccess(status,
        `✓ ${all.length} Termin${all.length === 1 ? '' : 'e'} aus ${sourceLabel} geladen — werden in der Matrix als belegt schraffiert.`,
        sources,
      );
      clearBtn.style.display = '';
      // Collapse the heavy import UI; users can re-open via "Ändern".
      renderCompact(all.length);
      compactBar.style.display = '';
      root.classList.add('is-collapsed');
      // Sobald importiert: kein leerer Einzeiler mehr — Compact-Bar zeigt Status.
      root.classList.remove('is-collapsed-empty');
    }
  };

  const importText = (label: string, text: string): boolean => {
    if (text.trim() === '') return false;
    if (looksLikeZipOrBinary(text)) {
      showError(status,
        `„${label}" sieht aus wie eine ZIP- oder Binärdatei. Bitte zuerst entpacken und die ` +
        `einzelne(n) .ics-Datei(en) reinziehen.`,
      );
      return false;
    }
    let parsed: BusyEvent[];
    try {
      // Wide range so config changes don't require re-import.
      const today = new Date();
      const start = today.toISOString().slice(0, 10);
      const future = new Date(today.getTime() + 730 * 86400 * 1000);
      const end = future.toISOString().slice(0, 10);
      parsed = parseICS(text, start, end);
    } catch (e) {
      showError(status, `„${label}": ${e instanceof Error ? e.message : 'Konnte nicht parsen.'}`);
      return false;
    }
    sources.set(label, parsed);
    return true;
  };

  applyBtn.addEventListener('click', () => {
    const text = textarea.value;
    hasAttemptedImport = true;
    if (importText('eingefügter Text', text)) {
      textarea.value = '';
    }
    flush();
  });
  textarea.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') applyBtn.click();
  });

  clearBtn.addEventListener('click', () => {
    sources.clear();
    flush({ reset: true });
    textarea.value = '';
    fileInput.value = '';
  });

  fileInput.addEventListener('change', async () => {
    const files = fileInput.files;
    if (!files) return;
    hasAttemptedImport = true;
    await ingestFiles(files, importText, status);
    flush();
    fileInput.value = '';
  });

  // Drag & drop (multi-file)
  const stopDrag = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
  for (const event of ['dragenter', 'dragover'] as const) {
    dropArea.addEventListener(event, (e) => {
      stopDrag(e);
      dropArea.classList.add('is-dragging');
    });
  }
  for (const event of ['dragleave', 'dragend'] as const) {
    dropArea.addEventListener(event, (e) => {
      stopDrag(e);
      dropArea.classList.remove('is-dragging');
    });
  }
  dropArea.addEventListener('drop', async (e) => {
    stopDrag(e);
    dropArea.classList.remove('is-dragging');
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    hasAttemptedImport = true;
    await ingestFiles(files, importText, status);
    flush();
  });

  // Drop direkt auf den Einzeiler — User muss nicht erst aufklappen.
  for (const event of ['dragenter', 'dragover'] as const) {
    collapsedBar.addEventListener(event, (e) => {
      stopDrag(e);
      collapsedBar.classList.add('is-dragging');
    });
  }
  for (const event of ['dragleave', 'dragend'] as const) {
    collapsedBar.addEventListener(event, (e) => {
      stopDrag(e);
      collapsedBar.classList.remove('is-dragging');
    });
  }
  collapsedBar.addEventListener('drop', async (e) => {
    stopDrag(e);
    collapsedBar.classList.remove('is-dragging');
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    hasAttemptedImport = true;
    // Sofort aufklappen, damit der User Status/Fehler sieht — auch wenn
    // das Parsen scheitert oder 0 Events liefert.
    root.classList.remove('is-collapsed-empty');
    await ingestFiles(files, importText, status);
    flush();
  });

  return root;
}

const MAX_ICS_FILE_BYTES = 12 * 1024 * 1024;         // 12 MB pro .ics — Apple-Calendar-Vollexport
const MAX_ZIP_FILE_BYTES = 24 * 1024 * 1024;         // 24 MB pro ZIP (deflated) — Google-Export
const MAX_ZIP_EXTRACTED_BYTES = 64 * 1024 * 1024;    // 64 MB total dekomprimiert — Zip-Bomb-Schutz
const MAX_ICS_PER_ZIP = 20;                          // sanity — auch große Multi-Kalender selten >10

/** Read each file. Akzeptiert .ics direkt ODER .zip (Google-Calendar-Export)
 *  — ZIPs werden im Browser entpackt, alle enthaltenen .ics dann einzeln
 *  importiert. Server bekommt nichts davon mit. */
async function ingestFiles(
  files: FileList,
  importText: (label: string, text: string) => boolean,
  status: HTMLElement,
): Promise<void> {
  for (const file of Array.from(files)) {
    const isZip = /\.zip$/i.test(file.name) || file.type === 'application/zip';
    if (isZip) {
      await ingestZip(file, importText, status);
      continue;
    }
    if (!/\.ics$/i.test(file.name) && file.type !== 'text/calendar') {
      showError(status, `„${file.name}" wirkt nicht wie eine .ics-Datei und wurde übersprungen.`);
      continue;
    }
    if (file.size > MAX_ICS_FILE_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      const maxMb = MAX_ICS_FILE_BYTES / 1024 / 1024;
      showError(status,
        `„${file.name}" ist mit ${mb} MB zu groß (Maximum ${maxMb} MB). ` +
        `Falls deine Datei größer ist, exportier einen kürzeren Zeitraum.`,
      );
      continue;
    }
    try {
      const text = await file.text();
      importText(file.name, text);
    } catch {
      showError(status, `„${file.name}" konnte nicht gelesen werden.`);
    }
  }
}

/** Entpackt ein Google-Calendar-ZIP im Browser und reicht jede enthaltene .ics
 *  weiter an importText. Schutzmaßnahmen gegen ZIP-Bombs:
 *    - Deflated-Größe ≤ MAX_ZIP_FILE_BYTES
 *    - Summe entpackter Größen ≤ MAX_ZIP_EXTRACTED_BYTES
 *    - Maximal MAX_ICS_PER_ZIP .ics pro ZIP
 *    - Pro .ics gilt weiterhin der MAX_ICS_FILE_BYTES-Cap */
async function ingestZip(
  file: File,
  importText: (label: string, text: string) => boolean,
  status: HTMLElement,
): Promise<void> {
  if (file.size > MAX_ZIP_FILE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    const maxMb = MAX_ZIP_FILE_BYTES / 1024 / 1024;
    showError(status,
      `„${file.name}" ist mit ${mb} MB zu groß als ZIP (Maximum ${maxMb} MB). ` +
      `Bitte einen kürzeren Zeitraum exportieren.`,
    );
    return;
  }
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    showError(status, `„${file.name}" konnte nicht als ZIP gelesen werden.`);
    return;
  }
  const icsEntries = Object.values(zip.files).filter((f) => !f.dir && /\.ics$/i.test(f.name));
  if (icsEntries.length === 0) {
    showError(status,
      `„${file.name}" enthält keine .ics-Dateien. Sicher, dass das ein Calendar-Export ist?`,
    );
    return;
  }
  if (icsEntries.length > MAX_ICS_PER_ZIP) {
    showError(status,
      `„${file.name}" enthält ${icsEntries.length} .ics-Dateien — Maximum sind ${MAX_ICS_PER_ZIP}. ` +
      `Bitte einen kleineren Export.`,
    );
    return;
  }
  let totalBytes = 0;
  for (const entry of icsEntries) {
    try {
      const text = await entry.async('string');
      totalBytes += text.length;
      if (totalBytes > MAX_ZIP_EXTRACTED_BYTES) {
        showError(status,
          `Inhalt von „${file.name}" überschreitet ${MAX_ZIP_EXTRACTED_BYTES / 1024 / 1024} MB ` +
          `entpackt — Import abgebrochen.`,
        );
        return;
      }
      // Label so anzeigen, dass der User erkennt, woraus es kam.
      const label = `${file.name}::${entry.name.split('/').pop() ?? entry.name}`;
      importText(label, text);
    } catch {
      showError(status, `Eintrag „${entry.name}" in „${file.name}" konnte nicht gelesen werden.`);
    }
  }
}

/** Quick heuristic: does the content start with the ZIP magic, or contain
 *  many non-printable bytes in the first chunk? Avoids feeding garbage to the
 *  parser. */
function looksLikeZipOrBinary(text: string): boolean {
  if (text.startsWith('PK') || text.startsWith('PK')) return true;
  // Sample first 256 chars; if more than 5 % non-printable, treat as binary.
  const sample = text.substring(0, 256);
  let weird = 0;
  for (let i = 0; i < sample.length; i++) {
    const c = sample.charCodeAt(i);
    if (c === 9 || c === 10 || c === 13) continue;
    if (c < 32 || c === 127) weird++;
  }
  return weird / Math.max(1, sample.length) > 0.05;
}

function showError(status: HTMLElement, msg: string): void {
  status.textContent = '✗ ' + msg;
  status.classList.remove('is-info');
  status.classList.add('is-error');
  status.style.display = '';
}

function showSuccess(
  status: HTMLElement,
  message: string,
  sources: Map<string, BusyEvent[]>,
): void {
  status.replaceChildren(document.createTextNode(message));
  if (sources.size > 0) {
    const list = el('ul', { class: 'calendar-source-list' });
    for (const [name, events] of sources) {
      list.append(el('li', {},
        el('span', { class: 'calendar-source-name' }, name),
        ' — ',
        el('span', { class: 'calendar-source-count' },
          `${events.length} Termin${events.length === 1 ? '' : 'e'}`),
      ));
    }
    status.append(list);
  }
  status.classList.add('is-info');
  status.classList.remove('is-error');
  status.style.display = '';
}

function renderHelp(): HTMLElement {
  const details = el('details', { class: 'calendar-help' });
  details.append(el('summary', { class: 'calendar-help-summary' },
    'Wo finde ich den Kalender-Export?'));

  const list = el('ul', { class: 'calendar-help-list' });

  list.append(
    el('li', {},
      el('strong', {}, 'Google Calendar: '),
      el('a', {
        href: 'https://calendar.google.com/calendar/u/0/r/settings/export',
        target: '_blank',
        rel: 'noopener noreferrer',
      }, 'Direkt zum Export ↗'),
      ' — lädt eine ',
      el('code', {}, '.zip'),
      '. Die kannst du ',
      el('strong', {}, 'direkt hier reinziehen'),
      ' — Slot entpackt sie im Browser.',
    ),
    el('li', {},
      el('strong', {}, 'Outlook (privat / Live): '),
      el('a', {
        href: 'https://outlook.live.com/calendar/0/options/calendar/SharedCalendars',
        target: '_blank',
        rel: 'noopener noreferrer',
      }, 'Geteilte Kalender öffnen ↗'),
      ' → unten „Veröffentlichen" → ICS herunterladen',
    ),
    el('li', {},
      el('strong', {}, 'Outlook (Office 365): '),
      el('a', {
        href: 'https://outlook.office.com/mail/options/calendar/SharedCalendars',
        target: '_blank',
        rel: 'noopener noreferrer',
      }, 'Geteilte Kalender öffnen ↗'),
    ),
    el('li', {},
      el('strong', {}, 'Apple Kalender (Mac): '),
      'in der App: ',
      el('em', {}, 'Datei → Exportieren → Exportieren'),
      ' (kein Web-Direktlink möglich)',
    ),
  );

  details.append(list);
  details.append(el('p', { class: 'calendar-help-tip' },
    'Tipp: Mehrere Dateien (z.B. Privat + Beruflich) kannst du auf einmal reinziehen — ' +
    'sie werden zusammengeführt.',
  ));

  return details;
}
